<template>
  <client-only>
    <div class="contacts container">
      <b-row>
        <b-col md="6" offset-md="3">
          <h2 class="custom-title mt-5">Contact Us</h2>
          <p>Leave a message</p>
          <b-form @submit.prevent="submitForm">
            <b-form-group>
              <div class="row mt-2 mb-3">
                <div class="col">
                  <h4 class="mb-2">Reason for your message</h4>
                  <CustomSelector
                    v-model="form.reason"
                    :options="reasonSelectorOptions"
                    placeholder="Select"
                    :state="validateState('reason')"
                  >
                  </CustomSelector>
                </div>
              </div>
              <div class="row mt-2 mb-3">
                <div class="col">
                  <h4 class="mb-2">Your name</h4>
                  <b-form-input
                    v-model="form.name"
                    class="custom-form-input"
                    placeholder="e.g. Joe Bloggs"
                    :state="validateState('name')"
                  ></b-form-input>
                </div>
              </div>
              <div class="row mt-2 mb-3">
                <div class="col">
                  <h4 class="mb-2">Your email</h4>
                  <b-form-input
                    v-model="form.email"
                    class="custom-form-input"
                    placeholder="e.g. name@mail.com"
                    :state="validateState('email')"
                  ></b-form-input>
                </div>
              </div>
              <div v-if="isOrderNumberFieldVisible" class="row mt-2 mb-3">
                <div class="col">
                  <h4 class="mb-2">Order number</h4>
                  <b-form-input
                    v-model="form.orderId"
                    class="custom-form-input"
                    placeholder="e.g. 1633619942741"
                    :state="validateState('orderId')"
                  ></b-form-input>
                </div>
              </div>
              <div class="row mt-2 mb-5">
                <div class="col">
                  <h4 class="mb-2">Your message</h4>
                  <b-form-textarea
                    v-model="form.comment"
                    class="custom-form-textarea"
                    placeholder="How can we help you today?"
                    rows="6"
                    max-rows="6"
                    :state="validateState('comment')"
                  ></b-form-textarea>
                </div>
              </div>
              <div class="row mt-5 mb-5 pt-5">
                <div class="col">
                  <Button type="submit" full-width>
                    Submit your details
                  </Button>
                </div>
              </div>
            </b-form-group>
          </b-form>
        </b-col>
      </b-row>
    </div>
  </client-only>
</template>

<script>
import { mapState } from 'vuex';
import { email, required, requiredIf } from 'vuelidate/lib/validators';

import Button from '@/components/Elements/Button';
import CustomSelector from '@/components/Elements/CustomSelector';

import { sendReCaptchaToken } from '@/services/captcha';
import { sendContactMessage } from '@/services/contact/index';

const emptyForm = {
  name: null,
  email: null,
  orderId: null,
  comment: null,
  reason: null
};

export default {
  name: 'ContactsPage',
  components: {
    Button,
    CustomSelector
  },
  data() {
    return {
      form: {
        ...emptyForm
      }
    };
  },
  head: {
    title: 'Contact Found',
    meta: [
      {
        hid: 'description',
        name: 'description',
        content:
          'Contact Found / Customer Service for support or for becoming a published creator.'
      }
    ]
  },

  computed: {
    ...mapState('config', ['config']),
    reasonSelectorOptions() {
      return [
        { value: 'placing_an_order', text: 'Placing an order' },
        { value: 'order_issue', text: 'Order Issue' },
        { value: 'feedback_suggestions', text: 'Feedback, suggestions' }
      ];
    },
    isOrderNumberFieldVisible() {
      return this.form.reason === 'order_issue';
    }
  },

  async mounted() {
    await this.$recaptcha.init();
  },

  validations() {
    return {
      form: {
        name: { required },
        email: { required, email },
        orderId: {
          required: requiredIf(() => {
            return this.isOrderNumberFieldVisible;
          })
        },
        comment: { required },
        reason: { required }
      }
    };
  },

  beforeDestroy() {
    this.$recaptcha.destroy();
  },

  methods: {
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    handleChange(event) {
      this.form.reason = event;
    },
    checkFormValidation() {
      this.$v.form.$touch();
      return !this.$v.form.$anyError;
    },
    async submitForm() {
      if (!this.checkFormValidation()) {
        this.$nextTick(() => {
          document.getElementsByClassName('is-invalid')[0]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        });
        return;
      }
      try {
        const token = await this.$recaptcha.execute('submit');
        const recaptcha = await sendReCaptchaToken(token);

        if (recaptcha.success) {
          const payload = this.form;

          if (!this.isOrderNumberFieldVisible) {
            delete payload.orderId;
          }

          await sendContactMessage(payload);
          this.form = { ...emptyForm };
          this.$v.form.$reset();
          this.$showToastMessage(
            'Your email has been sent successfully',
            'Success',
            'success'
          );
        } else {
          this.$showToastMessage('Captcha error', 'Error', 'danger');
        }
      } catch (e) {
        this.$showToastMessage(
          'Apologies but we are facing technical issues with this form. Please email your enquiry to hello@found.us',
          'Error',
          'danger'
        );
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../styles/components';

.contacts {
  h4 {
    font-family: Poppins-SemiBold, sans-serif;
    font-size: 18px;
    line-height: 24px;
  }
}
</style>
